// Formulario de Configuración de cobranza (plazo general + regla automática + plantillas).
// Funciones puras: estado del formulario ⇄ settings de billing, validación y payload.
//
// Los límites replican los DTOs de billing para avisar en el campo en vez de devolver un
// 400 genérico; billing sigue siendo la autoridad y revalida todo.

export const MIN_PAYMENT_TERM_DAYS = 1;
export const MAX_PAYMENT_TERM_DAYS = 365;
export const MAX_REMINDER_STEPS = 6;
/** La ventana de envío cierra a las 20:00 locales: la última hora de inicio es 19. */
export const MAX_SEND_HOUR_LOCAL = 19;
const DEFAULT_SEND_HOUR_LOCAL = 10;

const isTermDays = (v) =>
  Number.isInteger(v) && v >= MIN_PAYMENT_TERM_DAYS && v <= MAX_PAYMENT_TERM_DAYS;

// Clave estable de cada fila de paso (React la usa para no mezclar los campos al quitar una).
let stepRowSeq = 0;
const nextStepRowKey = () => {
  stepRowSeq += 1;
  return `step-${stepRowSeq}`;
};

/** Fila nueva de paso: días por completar y la plantilla por defecto en cada canal. */
export const newStepRow = (days = '') => ({
  key: nextStepRowKey(),
  days: String(days),
  emailTemplateId: null,
  whatsappTemplateId: null,
});

const templateIdOrNull = (value) => (typeof value === 'string' && value ? value : null);

/**
 * ¿Los settings traen las plantillas (billing con plantillas)? Uno anterior no manda
 * `stepTemplates` ni `defaultTemplateIds` y rechaza con 400 cualquier campo desconocido:
 * entonces el formulario no los muestra ni los manda.
 */
function hasTemplateFields(settings) {
  const defaults = settings?.defaultTemplateIds;
  return (
    Array.isArray(settings?.autoReminders?.stepTemplates) &&
    defaults !== null &&
    typeof defaults === 'object'
  );
}

/**
 * Estado del formulario a partir de `settings` (GET billing/collections/settings). Todo va
 * como lo necesita un control: números de texto libre como string. Los pasos son filas con
 * sus plantillas; billing indexa `stepTemplates` por días (no por posición), así que cada
 * fila toma la entrada de sus días.
 */
export function settingsToForm(settings) {
  const ar = settings?.autoReminders ?? {};
  const term = settings?.defaultPaymentTermDays;
  const templatesSupported = hasTemplateFields(settings);
  const byDays = new Map(
    (templatesSupported ? ar.stepTemplates : [])
      .filter((entry) => Number.isInteger(entry?.days))
      .map((entry) => [entry.days, entry]),
  );
  const stepsDays = Array.isArray(ar.stepsDays) ? ar.stepsDays : [];
  return {
    hasTerm: isTermDays(term),
    termDays: isTermDays(term) ? String(term) : '',
    enabled: ar.enabled === true,
    steps: stepsDays.map((days) => ({
      ...newStepRow(days),
      emailTemplateId: templateIdOrNull(byDays.get(days)?.emailTemplateId),
      whatsappTemplateId: templateIdOrNull(byDays.get(days)?.whatsappTemplateId),
    })),
    channels: {
      email: ar.channels?.email !== false,
      whatsapp: ar.channels?.whatsapp === true,
    },
    whatsappIntegrationId: ar.whatsappIntegrationId ?? null,
    sendHourLocal: Number.isInteger(ar.sendHourLocal) ? ar.sendHourLocal : DEFAULT_SEND_HOUR_LOCAL,
    weekdaysOnly: ar.weekdaysOnly !== false,
    templatesSupported,
    defaultTemplateIds: {
      email: templateIdOrNull(settings?.defaultTemplateIds?.email),
      whatsapp: templateIdOrNull(settings?.defaultTemplateIds?.whatsapp),
    },
    applyToLegacyUnpaid: false,
  };
}

/**
 * Filas de pasos → días válidos (ordenados y sin repetir) y errores por fila (claves i18n,
 * indexadas por `key`). Una fila repetida es un error y no se descarta en silencio: cada
 * fila lleva sus plantillas y no se puede elegir cuál vale.
 *
 * @returns {{ steps: number[], rowErrors: Record<string, string>, error: string|null }}
 */
export function parseStepRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const rowErrors = {};
  const seen = new Set();
  for (const row of list) {
    const text = String(row?.days ?? '').trim();
    const days = Number(text);
    if (!/^\d+$/.test(text) || !isTermDays(days)) {
      rowErrors[row.key] = 'Ingresá un número de días entre 1 y 365.';
    } else if (seen.has(days)) {
      rowErrors[row.key] = 'Ya hay un paso con esos días.';
    } else {
      seen.add(days);
    }
  }
  return {
    steps: [...seen].sort((a, b) => a - b),
    rowErrors,
    error: list.length > MAX_REMINDER_STEPS ? 'Máximo 6 pasos.' : null,
  };
}

/**
 * Errores por campo (claves i18n). Las reglas de consistencia son las de billing: con la
 * regla activa hacen falta pasos, al menos un canal y, con WhatsApp, un número. Una fila de
 * paso inválida bloquea aunque la regla esté apagada (lo guardado se usa al encenderla).
 *
 * @returns {{ termDays?: string, stepsDays?: string, stepRows?: Record<string, string>,
 *   channels?: string, whatsappIntegrationId?: string }}
 */
export function validateSettingsForm(form) {
  const errors = {};
  if (form.hasTerm && !isTermDays(Number(form.termDays))) {
    errors.termDays = 'Ingresá un plazo entre 1 y 365 días.';
  }
  const { steps, rowErrors, error: stepsError } = parseStepRows(form.steps);
  const hasRowErrors = Object.keys(rowErrors).length > 0;
  if (hasRowErrors) errors.stepRows = rowErrors;
  if (stepsError) errors.stepsDays = stepsError;
  if (form.enabled) {
    if (!stepsError && !hasRowErrors && steps.length === 0) {
      errors.stepsDays =
        'Para activar los recordatorios automáticos definí al menos un paso (días de atraso).';
    }
    if (!form.channels.email && !form.channels.whatsapp) {
      errors.channels =
        'Para activar los recordatorios automáticos elegí al menos un canal (correo o WhatsApp).';
    }
    if (form.channels.whatsapp && !form.whatsappIntegrationId) {
      errors.whatsappIntegrationId =
        'Elegí el número de WhatsApp desde el que se envían los recordatorios.';
    }
  }
  return errors;
}

/** Filas con días válidos, ordenadas por días; ante días repetidos vale la primera. */
function validStepRows(rows) {
  const byDays = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const text = String(row?.days ?? '').trim();
    const days = Number(text);
    if (/^\d+$/.test(text) && isTermDays(days) && !byDays.has(days)) byDays.set(days, row);
  }
  return [...byDays.entries()].sort(([a], [b]) => a - b);
}

/**
 * Configuración COMPLETA del formulario (base de `formToSettingsRequest`, que es el body de
 * PUT settings / POST settings/preview, y de la comparación de `isSettingsFormDirty`).
 * `applyToLegacyUnpaid` solo si se tildó.
 *
 * Con un billing que ya tiene plantillas se mandan también `stepTemplates` (una entrada por
 * paso, indexada por días: reemplaza la asignación completa) y `defaultTemplateIds`; `null`
 * es la plantilla por defecto del canal en un paso, y la del sistema en el default. Las
 * claves van en orden fijo: `isSettingsFormDirty` compara el JSON.
 */
export function formToSettingsPayload(form) {
  const rows = validStepRows(form.steps);
  const autoReminders = {
    enabled: form.enabled,
    stepsDays: rows.map(([days]) => days),
    channels: { email: !!form.channels.email, whatsapp: !!form.channels.whatsapp },
    whatsappIntegrationId: form.whatsappIntegrationId || null,
    sendHourLocal: Number(form.sendHourLocal),
    weekdaysOnly: !!form.weekdaysOnly,
  };
  const payload = {
    defaultPaymentTermDays: form.hasTerm ? Number(form.termDays) : null,
    autoReminders,
  };
  if (form.templatesSupported) {
    autoReminders.stepTemplates = rows.map(([days, row]) => ({
      days,
      emailTemplateId: row.emailTemplateId || null,
      whatsappTemplateId: row.whatsappTemplateId || null,
    }));
    payload.defaultTemplateIds = {
      email: form.defaultTemplateIds?.email || null,
      whatsapp: form.defaultTemplateIds?.whatsapp || null,
    };
  }
  if (form.applyToLegacyUnpaid) payload.applyToLegacyUnpaid = true;
  return payload;
}

const TEMPLATE_CHANNELS = ['email', 'whatsapp'];

/** Pasos con plantilla propia en algún canal: `[días, correo, WhatsApp]`, ordenados por días. */
const stepAssignments = (rows) =>
  validStepRows(rows)
    .filter(([, row]) => row.emailTemplateId || row.whatsappTemplateId)
    .map(([days, row]) => [days, row.emailTemplateId || null, row.whatsappTemplateId || null]);

/**
 * Body de PUT settings y de su vista previa: el de `formToSettingsPayload`, pero las
 * plantillas viajan SOLO si el operador las cambió en esta sesión respecto de lo cargado
 * (`initial`). Así un formulario abierto hace rato no revierte lo que otra sesión marcó desde
 * Plantillas ("Marcar por defecto" es un PUT parcial): billing conserva lo que no viene.
 *
 *   · `defaultTemplateIds`: solo los canales cambiados (billing los aplica canal por canal).
 *   · `stepTemplates`: completo si cambió alguna asignación (reemplaza la asignación entera);
 *     si no, se omite y billing realinea la que tiene con los pasos nuevos (un paso que se
 *     quita pierde su plantilla y uno nuevo queda con la por defecto).
 */
export function formToSettingsRequest(form, initial) {
  const payload = formToSettingsPayload(form);
  if (!form.templatesSupported || !initial) return payload;
  const changedDefaults = TEMPLATE_CHANNELS.filter(
    (channel) =>
      (form.defaultTemplateIds?.[channel] || null) !== (initial.defaultTemplateIds?.[channel] || null),
  );
  if (changedDefaults.length === 0) {
    delete payload.defaultTemplateIds;
  } else {
    payload.defaultTemplateIds = Object.fromEntries(
      changedDefaults.map((channel) => [channel, payload.defaultTemplateIds[channel]]),
    );
  }
  if (JSON.stringify(stepAssignments(form.steps)) === JSON.stringify(stepAssignments(initial.steps))) {
    delete payload.autoReminders.stepTemplates;
  }
  return payload;
}

/**
 * Filas como las ve el operador (días sin espacios y plantillas), sin importar el orden:
 * reordenarlas no cambia lo que se guarda.
 */
const rawStepRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) =>
      JSON.stringify([String(row?.days ?? '').trim(), row?.emailTemplateId || null, row?.whatsappTemplateId || null]),
    )
    .sort();

/** ¿El formulario cambió algo respecto de lo guardado? (el tilde de completar cuenta). */
export function isSettingsFormDirty(form, initial) {
  if (!initial) return false;
  if (form.applyToLegacyUnpaid) return true;
  const a = formToSettingsPayload({ ...form, applyToLegacyUnpaid: false });
  const b = formToSettingsPayload({ ...initial, applyToLegacyUnpaid: false });
  if (JSON.stringify(a) !== JSON.stringify(b)) return true;
  // Una fila que el payload descarta (días repetidos o inválidos) también es un cambio: si
  // no contara, "Revisar y guardar" quedaría deshabilitado sin decir por qué, y es al
  // revisar donde la validación marca la fila.
  return JSON.stringify(rawStepRows(form.steps)) !== JSON.stringify(rawStepRows(initial.steps));
}
